import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useRef, useState } from "react";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Mixpanel } from "@/lib/mixpanel";
import ExecutionEntryForm, {
  getExecutionEntryFormSchema,
  ExecutionEntryFormValues,
} from "./execution-entry-form";
import {
  useCreateMaintenanceExecutionEntry,
  useCreateMaintenanceExecutionEntryAttachment,
} from "@/services/mutations/maintenance";
import { Step } from "./execution-entry-form/constants";
import { useGetTaskById } from "@/services/queries/maintenance";

const AddExecutionEntryButton = ({
  vesselId,
  taskId,
  refetchTask,
}: {
  vesselId: string;
  taskId: string;
  refetchTask: () => void;
}) => {
  const { data: task } = useGetTaskById({ vesselId, id: taskId });
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<number>(Step.INITIAL);
  const queryClient = useQueryClient();
  const { mutateAsync: createExecutionEntryAttachment } =
    useCreateMaintenanceExecutionEntryAttachment();

  const defaultValues = useMemo(
    () => ({
      executionDate: new Date(),
      runningHoursAtExecution: undefined,
      notes: "",
      files: [],
    }),
    []
  );

  const form = useForm<ExecutionEntryFormValues>({
    resolver: zodResolver(
      getExecutionEntryFormSchema(task?.taskDetail.scheduleType)
    ),
    defaultValues,
  });

  // Hard guard against double-submit: a synchronous ref blocks duplicate requests even when rapid taps fire before React re-renders. Cleared only when the whole flow settles.
  const isSubmittingRef = useRef(false);

  const { mutate: addExecutionEntry, isPending: isCreatingEntry } =
    useCreateMaintenanceExecutionEntry({
      onSuccess: async ({ id }) => {
        if (form.getValues().files && form.getValues().files.length > 0) {
          // Uploads use allSettled (not Promise.all over fire-and-forget mutate): the
          // entry was already created, so a failed attachment must NOT reject
          // onSuccess (that would route to onError, keep the drawer open, and risk a
          // duplicate re-submit). We await all uploads, then surface any failure.
          const attachmentResults = await Promise.allSettled(
            form.getValues().files.map((file) =>
              createExecutionEntryAttachment({
                vesselId,
                taskId,
                executionEntryId: id,
                file: file as File,
              })
            )
          );
          if (attachmentResults.some((r) => r.status === "rejected")) {
            console.error(
              "Attachment upload failed",
              attachmentResults.filter((r) => r.status === "rejected")
            );
            toast({
              variant: "destructive",
              title: t("error_message.upload_failed"),
              description: t("error_message.upload_failed_description"),
            });
          }
        }

        queryClient.invalidateQueries({
          queryKey: ["maintenance-execution-entries"],
        });

        setOpen(false);
        form.reset();
        toast({
          title: t("success_message.add_execution_entry_title"),
          description: t("success_message.add_execution_entry_description"),
        });

        Mixpanel.track("Add Execution Entry Successfully");
        isSubmittingRef.current = false;
      },
      onError: (error) => {
        console.error(error);
        Mixpanel.track("Add Execution Entry Failed");
        isSubmittingRef.current = false;
      },
    });

  const onSubmit = (values: ExecutionEntryFormValues) => {
    // Block re-entry while a submission is already in flight.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    addExecutionEntry({
      vesselId,
      taskId,
      executionDate: values.executionDate,
      runningHoursAtExecution: values.runningHoursAtExecution!,
      notes: values.notes,
    });
  };

  return (
    <>
      <button
        data-testid="add-execution-entry-button"
        onClick={() => setOpen(true)}
        className="text-[15px] font-medium text-[#45D3EC]"
      >
        {t("button.add_execution_entry")}
      </button>

      <ExecutionEntryForm
        open={open}
        setOpen={setOpen}
        form={form}
        step={step}
        setStep={setStep}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        isSubmitting={isCreatingEntry}
        scheduleType={task?.taskDetail.scheduleType}
        task={task}
        vesselId={vesselId}
        onTaskUpdated={refetchTask}
      />
    </>
  );
};

export default AddExecutionEntryButton;
